import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ProcessorId } from '../../domain/tools/types';

const FAVORITES_KEY = 'satr:favorite-tools:v1';
const RECENTS_KEY = 'satr:recent-tools:v1';
const MAX_RECENT = 8;

interface RecentTool {
  readonly id: ProcessorId;
  readonly usedAt: number;
}

function parseArray<T>(key: string): T[] {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed as T[] : [];
  } catch {
    return [];
  }
}

function writeArray<T>(key: string, value: readonly T[]): void {
  localStorage.setItem(key, JSON.stringify(value));
  window.dispatchEvent(new CustomEvent('satr-preferences', { detail: key }));
}

export function useFavorites(): {
  favorites: ReadonlySet<ProcessorId>;
  toggleFavorite: (id: ProcessorId) => void;
} {
  const [values, setValues] = useState<ProcessorId[]>(() => parseArray<ProcessorId>(FAVORITES_KEY));

  useEffect(() => {
    const sync = (): void => setValues(parseArray<ProcessorId>(FAVORITES_KEY));
    const localSync = (event: Event): void => {
      if ((event as CustomEvent<string>).detail === FAVORITES_KEY) sync();
    };
    window.addEventListener('storage', sync);
    window.addEventListener('satr-preferences', localSync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('satr-preferences', localSync);
    };
  }, []);

  const toggleFavorite = useCallback((id: ProcessorId): void => {
    setValues((current) => {
      const next = current.includes(id) ? current.filter((entry) => entry !== id) : [...current, id];
      writeArray(FAVORITES_KEY, next);
      return next;
    });
  }, []);

  return { favorites: useMemo(() => new Set(values), [values]), toggleFavorite };
}

export function markToolUsed(id: ProcessorId): void {
  const current = parseArray<RecentTool>(RECENTS_KEY).filter((entry) => entry && entry.id !== id);
  writeArray(RECENTS_KEY, [{ id, usedAt: Date.now() }, ...current].slice(0, MAX_RECENT));
}

export function useRecentTools(): readonly RecentTool[] {
  const [recent, setRecent] = useState<RecentTool[]>(() => parseArray<RecentTool>(RECENTS_KEY));

  useEffect(() => {
    const sync = (): void => setRecent(parseArray<RecentTool>(RECENTS_KEY));
    const localSync = (event: Event): void => {
      if ((event as CustomEvent<string>).detail === RECENTS_KEY) sync();
    };
    window.addEventListener('storage', sync);
    window.addEventListener('satr-preferences', localSync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('satr-preferences', localSync);
    };
  }, []);

  return recent.filter((entry): entry is RecentTool => typeof entry.id === 'string' && typeof entry.usedAt === 'number');
}

export function clearToolHistory(): void {
  localStorage.removeItem(RECENTS_KEY);
  window.dispatchEvent(new CustomEvent('satr-preferences', { detail: RECENTS_KEY }));
}

export function clearFavorites(): void {
  localStorage.removeItem(FAVORITES_KEY);
  window.dispatchEvent(new CustomEvent('satr-preferences', { detail: FAVORITES_KEY }));
}
